import type { AutoConnection, RelationshipPair } from '../../entities/graph-document';
import type { ConnectionDisplayMode } from '../../ports/preferences-repository';
import { strongestConnectionsForNode } from '../auto-connections/derive-connections';
import {
	composeVisibleRelationshipEdges,
	selectRelationshipSummary,
	type ExpandedRelationshipReveal,
	type VisibleRelationshipEdge,
} from '../explicit-relationships';

export type GraphDisplayConnectionEmphasis = 'normal' | 'focused' | 'dimmed';

export interface GraphDisplayConnection extends AutoConnection {
	emphasis: GraphDisplayConnectionEmphasis;
}

export interface GraphDisplayEdges {
	connections: GraphDisplayConnection[];
	relationshipEdges: VisibleRelationshipEdge[];
	highlightedConnections: GraphDisplayConnection[];
}

export function composeGraphDisplayEdges({
	connections,
	displayMode,
	expandedRelationship = null,
	focusedNodeId = null,
	relationships,
}: {
	connections: AutoConnection[];
	displayMode: ConnectionDisplayMode;
	expandedRelationship?: ExpandedRelationshipReveal | null;
	focusedNodeId?: string | null;
	relationships: RelationshipPair[];
}): GraphDisplayEdges {
	const highlightedConnectionIds = new Set(
		focusedNodeId ? strongestConnectionsForNode(connections, focusedNodeId, 3).map((connection) => connection.connectionId) : [],
	);
	const relationshipSourceConnections = selectRelationshipSourceConnections(connections, displayMode);
	const visibleConnections = selectVisibleConnections(connections, displayMode, focusedNodeId, highlightedConnectionIds);
	const relationshipEdges = displayMode === 'explicit-only'
		? composeAllExplicitRelationshipEdges({
			connections: relationshipSourceConnections,
			expandedRelationship,
			focusedNodeId,
			relationships,
		})
		: composeVisibleRelationshipEdges({
		connections: relationshipSourceConnections,
		expandedRelationship,
		focusedNodeId,
		relationships,
	});

	return {
		connections: visibleConnections,
		relationshipEdges,
		highlightedConnections: visibleConnections.filter((connection) => connection.emphasis === 'focused'),
	};
}

function composeAllExplicitRelationshipEdges({
	connections,
	expandedRelationship,
	focusedNodeId,
	relationships,
}: {
	connections: AutoConnection[];
	expandedRelationship: ExpandedRelationshipReveal | null;
	focusedNodeId: string | null;
	relationships: RelationshipPair[];
}): VisibleRelationshipEdge[] {
	const connectionGroups = groupConnectionsByPairId(connections);

	return relationships
		.map((relationshipPair) => {
			const sourceNodeId = resolveRelationshipSourceNodeId(relationshipPair, focusedNodeId, expandedRelationship);
			const targetNodeId = relationshipPair.endpoints[0] === sourceNodeId
				? relationshipPair.endpoints[1]
				: relationshipPair.endpoints[0];
			const derivedConnections = connectionGroups.get(relationshipPair.pairId) ?? [];

			return {
				sourceNodeId,
				targetNodeId,
				derivedConnections,
				relationshipPair,
				summary: selectRelationshipSummary({
					pair: relationshipPair,
					sourceNodeId,
					targetNodeId,
					derivedConnections,
					visibilityMode: expandedRelationship?.pairId === relationshipPair.pairId ? 'expand-visible' : 'focus-visible',
				}),
			};
		})
		.sort((left, right) => {
			const leftFocused = focusedNodeId && (left.sourceNodeId === focusedNodeId || left.targetNodeId === focusedNodeId);
			const rightFocused = focusedNodeId && (right.sourceNodeId === focusedNodeId || right.targetNodeId === focusedNodeId);
			if (leftFocused !== rightFocused) {
				return leftFocused ? -1 : 1;
			}

			return left.relationshipPair.pairId.localeCompare(right.relationshipPair.pairId);
		});
}

function resolveRelationshipSourceNodeId(
	relationshipPair: RelationshipPair,
	focusedNodeId: string | null,
	expandedRelationship: ExpandedRelationshipReveal | null,
): string {
	if (expandedRelationship?.pairId === relationshipPair.pairId && relationshipPair.endpoints.includes(expandedRelationship.sourceNodeId)) {
		return expandedRelationship.sourceNodeId;
	}

	if (focusedNodeId && relationshipPair.endpoints.includes(focusedNodeId)) {
		return focusedNodeId;
	}

	return relationshipPair.endpoints[0];
}

function selectVisibleConnections(
	connections: AutoConnection[],
	displayMode: ConnectionDisplayMode,
	focusedNodeId: string | null,
	highlightedConnectionIds: Set<string>,
): GraphDisplayConnection[] {
	if (displayMode === 'explicit-only') {
		return [];
	}

	const filteredConnections = displayMode === 'all'
		? connections
		: connections.filter(isSharedTagsConnection);

	return filteredConnections.map((connection) => ({
		...connection,
		emphasis: connectionEmphasis(connection, displayMode, focusedNodeId, highlightedConnectionIds),
	}));
}

function selectRelationshipSourceConnections(connections: AutoConnection[], displayMode: ConnectionDisplayMode): AutoConnection[] {
	if (displayMode === 'explicit-only') {
		return [];
	}

	if (displayMode === 'tag-derived' || displayMode === 'tag-derived-focus') {
		return connections.filter(isSharedTagsConnection);
	}

	return connections;
}

function connectionEmphasis(
	connection: AutoConnection,
	displayMode: ConnectionDisplayMode,
	focusedNodeId: string | null,
	highlightedConnectionIds: Set<string>,
): GraphDisplayConnectionEmphasis {
	if (displayMode === 'tag-derived-focus' && focusedNodeId) {
		return connection.sourceNodeId === focusedNodeId || connection.targetNodeId === focusedNodeId ? 'focused' : 'dimmed';
	}

	return highlightedConnectionIds.has(connection.connectionId) ? 'focused' : 'normal';
}

function isSharedTagsConnection(connection: AutoConnection): boolean {
	return connection.reasonCode === 'shared-tags';
}

function groupConnectionsByPairId(connections: AutoConnection[]): Map<string, AutoConnection[]> {
	const groups = new Map<string, AutoConnection[]>();

	for (const connection of connections) {
		const pairId = [connection.sourceNodeId, connection.targetNodeId].sort((left, right) => left.localeCompare(right)).join('::');
		groups.set(pairId, [...(groups.get(pairId) ?? []), connection]);
	}

	return groups;
}
